import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";

import {
  Backdrop,
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
  TextField as TextFieldMui,
} from "@material-ui/core";

import {
  Edit,
  FileInput,
  FormTab,
  linkToRecord,
  Loading,
  required,
  SelectInput,
  TabbedForm,
  Toolbar,
  TopToolbar,
  useEditController,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { useUser } from "../../../authenticatorApp/UserContext";
import {
  ACCEPTED_FILES_ADVICE,
  AppType,
  CarrierTypes,
  InboundStatus,
  MAX_FILES,
  RA_REQUIRED,
  Workflow,
} from "../../../utils/constants";
import { handleFileChange } from "../../../utils/fileUtils";
import { resolveScheduledDateTime } from "../../../utils/dateUtils";
import { logError } from "../../../utils/appUtils";
import { getBackPath } from "../../../utils/utils";
import { resolveDefaultEventType } from "../../../managerApp/resources/eventTypes/EventTypeUtils";
import {
  AttachFileIcon,
  CreatedAtIcon,
} from "../../../components/common/iconography";
import { BackButton } from "../../../components/common/BackButton";
import { FileField } from "../../../components/common/FileField";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { YardDateTimeInput } from "../../../components/common/YardDateTimeInput";
import { NotesTab } from "../../../components/common/NotesTab";
import { Scheduler } from "../../../components/common/scheduler/Scheduler";
import { DialogSaveButton } from "./DialogSaveButton";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const validateRequired = required();
const validateRequiredFiles = (value) =>
  Array.isArray(value) && value.length > 0 ? undefined : RA_REQUIRED;

const fetchEventType = async (record, setEventType, setCanSchedule, notify) => {
  try {
    const { eventType, canSchedule } = await resolveDefaultEventType(
      record?.account?.inboundEventTypeId,
      Workflow.INBOUND,
    );
    setEventType(eventType);
    setCanSchedule(canSchedule);
  } catch (err) {
    logError("inbounds:InboundAdviceEdit:fetchEventType", err, notify);
  }
};

const ShipmentSetupTab = ({
  record,
  eventType,
  canSchedule,
  selectedTimeslot,
  setSelectedTimeslot,
}) => {
  const form = useForm();
  const notify = useNotify();
  const { appUser } = useUser();
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const yardCommodities =
    window.appUser?.reloadYard?.activeYardCommodities?.items ?? [];

  const handleSchedulerOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (canSchedule) setIsSchedulerOpen(true);
  };

  const ignoreEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleTimeslot = (timeslot) => {
    const scheduledDate = resolveScheduledDateTime(timeslot, {
      notify,
      scope: "inbounds:InboundAdviceEdit:handleTimeslot",
    });
    if (!scheduledDate) return;
    form.change("scheduledDateTime", scheduledDate);
    setSelectedTimeslot(timeslot);
  };

  const getSchedulerHelperText = () =>
    canSchedule
      ? false
      : `Contact ${appUser?.reloadYard?.name} to setup an event type.`;

  return (
    <Grid container direction="row" spacing={3}>
      <Grid item container direction="column" spacing={0} lg={8}>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12} md={6}>
            <TextFieldMui
              label="Transload Yard"
              margin="dense"
              variant="outlined"
              fullWidth
              value={appUser?.reloadYard?.name || ""}
              disabled
              helperText={false}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <YardDateTimeInput
              source="scheduledDateTime"
              label="Schedule Delivery"
              variant="outlined"
              fullWidth
              disabled={!canSchedule}
              onClick={handleSchedulerOpen}
              onKeyPress={ignoreEvent}
              options={{
                readOnly: true,
                InputProps: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <CreatedAtIcon onClick={handleSchedulerOpen} />
                    </InputAdornment>
                  ),
                },
              }}
              helperText={getSchedulerHelperText()}
            />
            {canSchedule && (
              <Scheduler
                eventType={eventType}
                workflow={Workflow.INBOUND}
                account={record.account}
                appType={AppType.CUSTOMER}
                open={isSchedulerOpen}
                setIsOpen={setIsSchedulerOpen}
                resettable={false}
                selectedTimeslot={selectedTimeslot}
                setSelectedTimeslot={handleTimeslot}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              source="carrierType"
              label="Carrier Type"
              variant="outlined"
              fullWidth
              choices={CarrierTypes}
              validate={validateRequired}
              helperText={false}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              source="reloadYardCommodityId"
              label="Load Type"
              variant="outlined"
              fullWidth
              choices={yardCommodities}
              optionText={(choice) =>
                `${choice.commodity.name} - ${choice.units}`
              }
              optionValue="id"
              validate={validateRequired}
              helperText={false}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item container direction="row" spacing={0} xs={false} md={4}>
        <Grid item xs={12}>
          <FileInput
            source="attachments"
            label="Inbound Advice Attachments"
            variant="outlined"
            fullWidth
            validate={validateRequiredFiles}
            helperText="Accepted files: PNG, JPEG, PDF, or TIFF."
            placeholder={
              <p>
                <AttachFileIcon fontSize="large" /> <br /> Drop files to upload,
                or click to select.
              </p>
            }
            accept={ACCEPTED_FILES_ADVICE}
            multiple
            maxFiles={MAX_FILES}
            options={{ maxFiles: MAX_FILES }}
            onChange={(value) =>
              handleFileChange("attachments", value, form, notify)
            }
          >
            <FileField source="src" title="title" target="_blank" />
          </FileInput>
        </Grid>
      </Grid>
    </Grid>
  );
};

const EditActions = ({ basePath }) => (
  <TopToolbar>
    <BackButton basePath={basePath} />
  </TopToolbar>
);

const EditToolbar = ({ record, backPath, selectedTimeslot, ...props }) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);

  return (
    <Toolbar {...props}>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogSaveButton
        {...props}
        record={record}
        backPath={backPath}
        selectedTimeslot={selectedTimeslot}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
      />
    </Toolbar>
  );
};

export const InboundAdviceEdit = (props) => {
  const notify = useNotify();
  const { record } = useEditController(props);
  const backPath = getBackPath(props);
  const [eventType, setEventType] = useState(record?.timeslot?.eventType);
  const [canSchedule, setCanSchedule] = useState(
    Boolean(record?.timeslot?.eventType),
  );
  const [selectedTimeslot, setSelectedTimeslot] = useState();

  useEffect(() => {
    if (record?.timeslot) {
      // Editing an existing booking: keep its event type.
      setSelectedTimeslot(record.timeslot);
      setEventType(record.timeslot.eventType);
      setCanSchedule(Boolean(record.timeslot.eventType));
    } else if (record) {
      fetchEventType(record, setEventType, setCanSchedule, notify);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  if (!record) return <Loading />;
  // This form is the resubmit path for a rejected advice only — its save stamps
  // status = ADVICE_REVIEW. `edit` is registered for the whole resource, so a
  // hand-typed /inboundShipments/:id URL would otherwise let a customer drag a
  // live shipment back into advice review. Anything else is read-only (show).
  if (record.status !== InboundStatus.ADVICE_REJECTED)
    return <Redirect to={`${linkToRecord(props.basePath, props.id)}/show`} />;
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Inbound Advice" />}
      mutationMode="pessimistic"
      actions={<EditActions {...props} backPath={backPath} />}
    >
      <TabbedForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <EditToolbar
            {...props}
            record={record}
            backPath={backPath}
            selectedTimeslot={selectedTimeslot}
          />
        }
      >
        <FormTab label="Inbound Advice">
          <ShipmentSetupTab
            {...props}
            record={record}
            eventType={eventType}
            canSchedule={canSchedule}
            selectedTimeslot={selectedTimeslot}
            setSelectedTimeslot={setSelectedTimeslot}
          />
        </FormTab>
        <FormTab label="Notes">
          <NotesTab />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};
